/**
 * AiServiceStatusBanner — red banner shown to supervisors and admins
 * (NOT cleaners) when the Anthropic circuit breaker is OPEN. Tells
 * supervisors to expect AI-override submissions and manually verify
 * photo coverage before approving.
 *
 * Cleaners are intentionally excluded: knowing AI is not watching could
 * tempt corner-cutting on the upload set. The cleaner learns at the
 * moment of submit via the red BlockingPanel in TaskActionBar (Option
 * A), which is the right time to surface it. Defense-in-depth: the
 * RLS policy on ai_service_status also restricts SELECT to supervisor
 * / contractor_admin / super_admin.
 *
 * Hidden when state is CLOSED or HALF_OPEN. HALF_OPEN means a probe is
 * in flight; the next call's success will close the breaker; no banner
 * during recovery so users don't see flicker.
 */

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useRole }            from '@/hooks/useAuth'
import { useAiServiceStatus } from '@/hooks/useAiServiceStatus'

const BANNER_ROLES = new Set(['supervisor', 'contractor_admin', 'super_admin'])

export function AiServiceStatusBanner(): ReactNode {
  const { t }    = useTranslation()
  const role     = useRole()
  const { data } = useAiServiceStatus()

  if (!role || !BANNER_ROLES.has(role)) return null
  if (!data || data.state !== 'OPEN') return null

  return (
    <div
      data-testid="ai-service-status-banner"
      role="status"
      aria-live="polite"
      style={{
        background:   'var(--brand-danger-subtle, #fee2e2)',
        color:        'var(--brand-danger, #991b1b)',
        borderBottom: '1px solid var(--brand-danger, #b91c1c)',
        padding:      '0.5rem 1rem',
        fontSize:     '0.85rem',
        fontWeight:   500,
        textAlign:    'center',
      }}
    >
      <strong>{t('aiStatus.bannerTitle')}</strong> {t('aiStatus.bannerDetail')}
    </div>
  )
}
